import { Link } from 'react-router-dom';
import PageHero from '../components/ui/PageHero';
import {
  LuOctagonAlert, LuFlame, LuEye, LuActivity, LuWrench,
  LuKeyRound, LuCamera, LuZap, LuShieldCheck, LuCpu,
  LuArrowRight, LuTrophy, LuBrainCircuit, LuCircleCheck,
  LuClock, LuCalendar, LuRefreshCw, LuSettings,
  LuDownload, LuFileText, LuLock, LuRadio, LuUsers,
} from 'react-icons/lu';

const IMG = {
  hero:        'https://images.unsplash.com/photo-1605379399642-870262d3d051?w=900&q=80&auto=format',
  maintenance: 'https://images.unsplash.com/photo-1581094794329-c8112a89af12?w=700&q=80&auto=format',
  emergency:   'https://images.unsplash.com/photo-1504711434969-e33886168f5c?w=700&q=80&auto=format',
  sensors:     'https://i.pinimg.com/1200x/8f/ed/fa/8fedfa5f7234750fc5942a7adc7b96fe.jpg',
  control:     'https://images.unsplash.com/photo-1518770660439-4636190af475?w=700&q=80&auto=format',
  estop:       'https://images.unsplash.com/photo-1581092160607-ee22621dd758?w=600&q=80&auto=format',
  fire:        'https://i.pinimg.com/736x/8a/b8/0e/8ab80e4bf05f6a97f73c928937e61ca2.jpg',
  cctv:        'https://images.unsplash.com/photo-1557597774-9d273605dfa9?w=600&q=80&auto=format',
  failure:     'https://images.unsplash.com/photo-1436491865332-7a61a109cc05?w=700&q=80&auto=format',
  response:    'https://images.unsplash.com/photo-1521737711867-e3b97375f902?w=700&q=80&auto=format',
  predictive:  'https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=700&q=80&auto=format',
  access:      'https://i.pinimg.com/1200x/47/36/9e/47369e55c2242a4c712ef3f7055ea89b.jpg',
  speed:       'https://i.pinimg.com/736x/ea/28/06/ea28063c4d6159b6b852f5af2e3d516a.jpg',
  EP:          'https://i.pinimg.com/1200x/39/75/ff/3975ffdbb5c871cb37ae88f9ee9fd293.jpg'
};

const SAFETY_ITEMS = [
  {
    icon: <LuOctagonAlert size={24} />,
    title: 'Emergency Stop Buttons',
    desc: 'Strategically placed throughout the system for instant full-stop activation at any point along the belt network.',
    img: IMG.estop,
    color: 'var(--danger)', bg: 'rgba(220,38,38,.08)', border: 'rgba(220,38,38,.2)',
  },
  {
    icon: <LuFlame size={24} />,
    title: 'Fire Detection',
    desc: 'Smoke and heat sensors integrated along the entire belt network trigger automatic alerts and suppression systems.',
    img: IMG.fire,
    color: 'var(--warning)', bg: 'rgba(217,119,6,.08)', border: 'rgba(217,119,6,.2)',
  },
  {
    icon: <LuEye size={24} />,
    title: 'Obstacle Sensors',
    desc: 'Detect jams, foreign objects, and misplaced bags in milliseconds before any damage can occur to equipment or bags.',
    img: IMG.sensors,
    color: 'var(--primary)', bg: 'var(--primary-pale)', border: 'rgba(124,58,237,.2)',
  },
  {
    icon: <LuActivity size={24} />,
    title: 'Speed Monitoring',
    desc: 'Continuous checks prevent dangerous acceleration or sudden stops that could damage bags or injure equipment.',
    img: IMG.speed,
    color: 'var(--accent)', bg: 'var(--accent-pale)', border: 'rgba(8,145,178,.2)',
  },
  {
    icon: <LuWrench size={24} />,
    title: 'Preventive Maintenance',
    desc: 'Scheduled inspections and proactive part replacements minimise unplanned downtime significantly year-round.',
    img: IMG.maintenance,
    color: 'var(--accent-3)', bg: 'var(--accent-3-pale)', border: 'rgba(5,150,105,.2)',
  },
  {
    icon: <LuKeyRound size={24} />,
    title: 'Zone Access Control',
    desc: 'Restricted access zones prevent tampering, theft, and unauthorised interference with the baggage system.',
    img: IMG.access,
    color: 'var(--accent-2)', bg: 'var(--accent-2-pale)', border: 'rgba(219,39,119,.2)',
  },
  {
    icon: <LuCamera size={24} />,
    title: 'CCTV Surveillance',
    desc: '24/7 camera monitoring of all belt areas, sorting rooms, and tunnel access points for full operational visibility.',
    img: IMG.cctv,
    color: 'var(--primary)', bg: 'var(--primary-pale)', border: 'rgba(124,58,237,.2)',
  },
  {
    icon: <LuZap size={24} />,
    title: 'Electrical Protection',
    desc: 'Surge protection, earthing systems, and circuit breakers protect all electrical and electronic components from damage.',
    img: IMG.EP,
    color: 'var(--accent)', bg: 'var(--accent-pale)', border: 'rgba(8,145,178,.2)',
  },
];

const STOP_EFFECTS = [
  'All bags halt movement throughout the affected zone immediately, preventing further damage or safety risks',
  'Baggage flow for departing flights is automatically rerouted to redundant backup lines',
  'Control room receives real-time alerts with exact fault location and severity level within 1 second',
  'Maintenance technicians are dispatched within 2 minutes with full diagnostic information',
  'Manual baggage handling teams activate to ensure critical connections are maintained',
  'Passengers experience minimal disruption thanks to pre-planned contingency protocols',
];

const CAUSES = [
  'Motor or drive failure', 'Sensor malfunction', 'Power supply interruption',
  'Physical belt jam', 'Software control error', 'Foreign object obstruction',
  'Belt misalignment', 'Roller bearing failure', 'RFID reader outage',
  'Diverter mechanism fault', 'Conveyor belt tear', 'Air conditioning failure',
];

const FAILURE_SCENARIOS = [
  {
    title: 'Single Belt Failure',
    impact: 'Low',
    response: 'Automatic reroute to parallel belt within 3 seconds',
    recovery: 'Full recovery within 10 minutes',
    icon: <LuActivity size={20} />,
    color: 'var(--accent-3)',
  },
  {
    title: 'Zone Failure',
    impact: 'Medium',
    response: 'Multi-belt reroute + manual backup activated',
    recovery: 'Full recovery within 30 minutes',
    icon: <LuEye size={20} />,
    color: 'var(--accent)',
  },
  {
    title: 'System-Wide Outage',
    impact: 'High',
    response: 'Full manual mode + emergency protocols',
    recovery: 'Full recovery within 2 hours',
    icon: <LuOctagonAlert size={20} />,
    color: 'var(--danger)',
  },
];

const BACKUP_STEPS = [
  { icon: <LuActivity size={18} />,   title: 'Problem Detected',         detail: 'Sensors flag the fault instantly and raise an automated alert in the BMS control system.' },
  { icon: <LuEye size={18} />,        title: 'Control Room Alerted',     detail: 'The BMS notifies operators with exact location, fault type, and severity level.' },
  { icon: <LuSettings size={18} />,   title: 'Maintenance Dispatched',   detail: 'On-site engineers are paged and en route within minutes of fault detection.' },
  { icon: <LuRefreshCw size={18} />,  title: 'Backup Lines Activated',   detail: 'Redundant belt routes are automatically switched on to maintain bag flow.' },
  { icon: <LuWrench size={18} />,     title: 'Manual Teams Deployed',    detail: 'Baggage handlers manually process bags from the affected section.' },
  { icon: <LuCircleCheck size={18} />,title: 'Operations Resume',        detail: 'Once repaired and tested, the system resumes full automated operation.' },
];

const MAINT_SCHEDULE = [
  {
    icon: <LuClock size={20} />,
    freq: 'Daily',
    color: 'var(--primary)', bg: 'var(--primary-pale)',
    tasks: ['Visual belt inspections for wear & tear', 'Sensor status verification & cleaning', 'Lubrication of critical moving parts', 'Control panel diagnostics & log review', 'Emergency stop button testing'],
  },
  {
    icon: <LuCalendar size={20} />,
    freq: 'Weekly',
    color: 'var(--accent)', bg: 'var(--accent-pale)',
    tasks: ['Motor current & temperature analysis', 'Roller bearing inspection & replacement', 'Control system software diagnostics', 'RFID reader calibration & testing', 'Belt alignment verification'],
  },
  {
    icon: <LuRefreshCw size={20} />,
    freq: 'Monthly',
    color: 'var(--accent-3)', bg: 'var(--accent-3-pale)',
    tasks: ['Full system performance audit', 'Belt tension & tracking adjustment', 'Fire suppression system testing', 'Security access log review', 'Predictive maintenance model validation'],
  },
  {
    icon: <LuSettings size={20} />,
    freq: 'Annual',
    color: 'var(--accent-2)', bg: 'var(--accent-2-pale)',
    tasks: ['Complete system overhaul', 'Motor & gearbox replacements', 'Software updates & security patches', 'Full infrastructure review & upgrade planning', 'Staff training & certification refresh'],
  },
];

const PREDICTIVE_MAINTENANCE = [
  { metric: 'Motor Temperature', threshold: '85°C', action: 'Alert & schedule inspection', lead: '7 days' },
  { metric: 'Belt Vibration', threshold: '4.5 mm/s', action: 'Alignment adjustment', lead: '14 days' },
  { metric: 'Roller Noise', threshold: '75 dB', action: 'Bearing replacement', lead: '10 days' },
  { metric: 'RFID Read Rate', threshold: '98%', action: 'Reader calibration', lead: '3 days' },
  // { metric: 'Belt Tension', threshold: '±10%', action: 'Tensioner adjustment', lead: '5 days' },
];

const STATS = [
  { n: '99.95%', l: 'Target Uptime', d: 'Less than 4.5 hrs downtime/year' },
  { n: '<4 min',  l: 'Response Time', d: 'Avg. fault detection to response' },
  { n: '2 weeks', l: 'Predictive Lead', d: 'AI flags failures in advance' },
  { n: '8 layers',l: 'Safety Depth',   d: 'Redundant protection systems' },
];

function Safety() {
  return (
    <div className="page-enter">
      <PageHero
        tag="Safety First"
        title={<>Built-In Safety &amp; <span className="text-gradient">Emergency Systems</span></>}
        subtitle="Every airport conveyor system is engineered with multiple layers of protection, redundancy, and rapid-response backup systems to ensure continuous, safe operation."
        breadcrumb={[{ label: 'Safety & Emergency' }]}
        bgColor={`radial-gradient(ellipse 65% 55% at 65% 35%, rgba(220,38,38,.06) 0%, transparent 60%),
                  radial-gradient(ellipse 50% 40% at 20% 75%, rgba(5,150,105,.05) 0%, transparent 55%), var(--bg)`}
      >
        <div className="page-hero-img-card">
          <img src={IMG.hero} alt="Engineer maintaining airport conveyor belt system" loading="lazy" />
        </div>
      </PageHero>

      {/* ══ STATS STRIP ══ */}
      <div className="safety-stats-strip">
        <div className="container">
          <div className="safety-stats-grid">
            {STATS.map((s, i) => (
              <div key={i} className="safety-stat-item">
                <div className="ssi-num">{s.n}</div>
                <div className="ssi-label">{s.l}</div>
                <div className="ssi-desc">{s.d}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ══ SAFETY FEATURES GRID ══ */}
      <section className="page-section bg-dark-2">
        <div className="container">
          <div className="text-center reveal" style={{ marginBottom: 52 }}>
            <div className="section-tag">Protection Layers</div>
            <h2 className="section-title">Eight Layers of Safety Engineering</h2>
            <p className="section-sub" style={{ margin: '0 auto' }}>
              Eight independent safety systems keep passengers, staff, and bags protected
              at every stage of the baggage handling process — 24 hours a day.
            </p>
          </div>

          <div className="safety-features-grid">
            {SAFETY_ITEMS.map((item, i) => (
              <div
                key={i}
                className={`sfg-card reveal delay-${(i % 4) + 1}`}
                style={{ '--sfg-border': item.border, '--sfg-bg': item.bg }}
              >
                {item.img && (
                  <div className="sfg-img">
                    <img src={item.img} alt={item.title} loading="lazy" />
                    <div className="sfg-img-icon" style={{ background: item.color }}>
                      {item.icon}
                    </div>
                  </div>
                )}
                <div className={`sfg-body${item.img ? '' : ' sfg-body-no-img'}`}
                  style={!item.img ? { background: item.bg, borderColor: item.border } : {}}>
                  {!item.img && (
                    <div className="sfg-icon-wrap" style={{ color: item.color, background: 'var(--card-bg)', border: `1px solid ${item.border}` }}>
                      {item.icon}
                    </div>
                  )}
                  <div className="sfg-title">{item.title}</div>
                  <div className="sfg-desc">{item.desc}</div>
                </div>
              </div>
            ))}
          </div>

          {/* IoT sensor banner */}
          <div className="safety-sensor-banner reveal">
            <div className="ssb-img">
              <img src={IMG.sensors} alt="IoT sensors monitoring belt systems" loading="lazy" />
            </div>
            <div className="ssb-content">
              <div className="section-tag" style={{ alignSelf: 'flex-start' }}>Live Monitoring</div>
              <h3 style={{ fontFamily: 'var(--font-display)', fontSize: '1.3rem', fontWeight: 800, color: 'var(--text-2)', margin: '8px 0' }}>
                Real-Time IoT Sensor Network
              </h3>
              <p style={{ fontSize: '.9rem', color: 'var(--text-muted)', lineHeight: 1.8 }}>
                Hundreds of IoT sensors stream live data every second — motor temperatures, belt tensions,
                bag presence, and system health — directly into the central operations dashboard for
                instant fault detection and predictive maintenance alerts.
              </p>
              <div style={{ display: 'flex', gap: 20, marginTop: 16, flexWrap: 'wrap' }}>
                {[
                  { n: '500+', l: 'Sensors per terminal' },
                  { n: '1 sec', l: 'Data update frequency' },
                  { n: '24/7',  l: 'Continuous monitoring' },
                ].map((stat, i) => (
                  <div key={i} style={{ textAlign: 'center' }}>
                    <div style={{ fontFamily: 'var(--font-display)', fontSize: '1.3rem', fontWeight: 800, color: 'var(--primary)' }}>{stat.n}</div>
                    <div style={{ fontSize: '.74rem', color: 'var(--text-muted)', fontWeight: 600 }}>{stat.l}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <div className="divider" />

      {/* ══ FAILURE SCENARIOS SECTION ══ */}
      <section className="page-section bg-gradient-red" id="emergency">
        <div className="container">
          <div className="text-center reveal" style={{ marginBottom: 52 }}>
            <div className="section-tag">Failure Scenarios</div>
            <h2 className="section-title">Prepared for Every Possibility</h2>
            <p className="section-sub" style={{ margin: '0 auto' }}>
              Airport conveyor systems are engineered with multiple layers of redundancy
              to ensure operations continue smoothly even when components fail.
            </p>
          </div>

          {/* Failure scenarios cards */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 20, marginBottom: 40 }}>
            {FAILURE_SCENARIOS.map((scenario, i) => (
              <div key={i} className="reveal delay-1" style={{
                background: 'var(--card-bg)',
                borderRadius: 16,
                padding: 24,
                border: '1px solid var(--border-light)',
                boxShadow: 'var(--shadow-sm)',
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
                  <div style={{ width: 48, height: 48, borderRadius: 12, background: `${scenario.color}15`, display: 'flex', alignItems: 'center', justifyContent: 'center', color: scenario.color }}>
                    {scenario.icon}
                  </div>
                  <div>
                    <div style={{ fontWeight: 800, fontSize: '1.1rem', color: 'var(--text-2)' }}>{scenario.title}</div>
                    <div style={{ fontSize: '.78rem', color: 'var(--text-muted)' }}>Impact: {scenario.impact}</div>
                  </div>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                  <div style={{ padding: 12, background: 'var(--bg-4)', borderRadius: 10 }}>
                    <div style={{ fontSize: '.78rem', fontWeight: 700, color: 'var(--accent)', marginBottom: 4 }}>Response</div>
                    <div style={{ fontSize: '.86rem', color: 'var(--text-3)' }}>{scenario.response}</div>
                  </div>
                  <div style={{ padding: 12, background: 'var(--bg-4)', borderRadius: 10 }}>
                    <div style={{ fontSize: '.78rem', fontWeight: 700, color: 'var(--accent-3)', marginBottom: 4 }}>Recovery Time</div>
                    <div style={{ fontSize: '.86rem', color: 'var(--text-3)' }}>{scenario.recovery}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Two-column: effects + response */}
          <div className="emg-layout">

            {/* Left: image + immediate effects */}
            <div className="reveal-left">
              <div className="emg-img-card">
                <img src={IMG.failure} alt="Airport emergency response operations" loading="lazy" />
                <div className="emg-img-overlay">
                  <LuOctagonAlert size={16} style={{ color: '#fff' }} />
                  <span>Contingency protocols activated</span>
                </div>
              </div>
              <div className="emg-card emg-danger" style={{ marginTop: 20 }}>
                <h3>
                  <LuOctagonAlert size={20} style={{ color: 'var(--danger)', flexShrink: 0 }} />
                  Immediate System Response
                </h3>
                <ul className="emg-list">
                  {STOP_EFFECTS.map((e, i) => <li key={i}>{e}</li>)}
                </ul>
                <div style={{ marginTop: 20, borderTop: '1px solid var(--bg-4)', paddingTop: 16 }}>
                  <div style={{ fontSize: '.8rem', fontWeight: 700, color: 'var(--warning)', marginBottom: 10, display: 'flex', alignItems: 'center', gap: 6 }}>
                    <LuOctagonAlert size={13} /> Potential Failure Causes
                  </div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                    {CAUSES.map((c, i) => (
                      <span key={i} style={{ background: 'rgba(217,119,6,.08)', border: '1px solid rgba(217,119,6,.2)', color: 'var(--warning)', fontSize: '.74rem', padding: '3px 10px', borderRadius: 100 }}>{c}</span>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Right: response steps */}
            <div className="reveal-right">
              <div className="emg-card emg-success">
                <h3>
                  <LuShieldCheck size={20} style={{ color: 'var(--accent-3)', flexShrink: 0 }} />
                  6-Step Rapid Response Protocol
                </h3>
                <p style={{ fontSize: '.86rem', color: 'var(--text-muted)', marginBottom: 20, lineHeight: 1.7 }}>
                  Every airport maintains trained rapid-response teams and redundant systems
                  to restore full operations with minimal passenger disruption.
                </p>
                <div className="emg-steps-list">
                  {BACKUP_STEPS.map((s, i) => (
                    <div key={i} className="emg-step">
                      <div className="emg-step-num">{i + 1}</div>
                      <div className="emg-step-icon" style={{ color: 'var(--accent-3)' }}>{s.icon}</div>
                      <div className="emg-step-text">
                        <div className="emg-step-title">{s.title}</div>
                        <div className="emg-step-detail">{s.detail}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Control room image */}
              <div className="emg-control-img">
                <img src={IMG.response} alt="Baggage control room monitoring screens" loading="lazy" />
                <div className="emg-control-caption">
                  <LuActivity size={13} /> 24/7 control room oversight with AI-assisted monitoring
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <div className="divider" />

      {/* ══ KEEPING THE SYSTEM RUNNING SECTION ══ */}
      <section className="page-section bg-dark-2">
        <div className="container">
          <div className="text-center reveal" style={{ marginBottom: 52 }}>
            <div className="section-tag">Maintenance Programme</div>
            <h2 className="section-title">Keeping the System Running 24/7</h2>
            <p className="section-sub" style={{ margin: '0 auto' }}>
              A comprehensive, multi-layered maintenance strategy combines preventive, predictive,
              and corrective measures to ensure uninterrupted airport operations.
            </p>
          </div>

          {/* Predictive maintenance metrics */}
          <div className="reveal" style={{ marginBottom: 40 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
              <div style={{ width: 40, height: 40, borderRadius: 10, background: 'var(--accent-pale)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--accent)' }}>
                <LuBrainCircuit size={20} />
              </div>
              <div>
                <div style={{ fontWeight: 800, fontSize: '1.1rem', color: 'var(--text-2)' }}>Predictive Maintenance Monitoring</div>
                <div style={{ fontSize: '.8rem', color: 'var(--text-muted)' }}>AI-powered failure prediction with advanced IoT sensors</div>
              </div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: 12 }}>
              {PREDICTIVE_MAINTENANCE.map((item, i) => (
                <div key={i} style={{
                  background: 'var(--card-bg)',
                  border: '1px solid var(--border-light)',
                  borderRadius: 12,
                  padding: 16,
                }}>
                  <div style={{ fontWeight: 700, fontSize: '.9rem', color: 'var(--text-2)', marginBottom: 8 }}>{item.metric}</div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '.8rem', marginBottom: 4 }}>
                    <span style={{ color: 'var(--text-muted)' }}>Threshold:</span>
                    <span style={{ color: 'var(--accent)', fontWeight: 600 }}>{item.threshold}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '.8rem', marginBottom: 4 }}>
                    <span style={{ color: 'var(--text-muted)' }}>Action:</span>
                    <span style={{ color: 'var(--accent-3)', fontWeight: 600 }}>{item.action}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '.8rem' }}>
                    <span style={{ color: 'var(--text-muted)' }}>Prediction Lead:</span>
                    <span style={{ color: 'var(--primary)', fontWeight: 600 }}>{item.lead}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="maint-schedule-grid">
            {MAINT_SCHEDULE.map((m, i) => (
              <div key={i} className={`maint-sched-card reveal delay-${i + 1}`}>
                <div className="msc-header" style={{ background: m.bg }}>
                  <div className="msc-icon" style={{ color: m.color }}>{m.icon}</div>
                  <div className="msc-freq" style={{ color: m.color }}>{m.freq}</div>
                </div>
                <ul className="msc-tasks">
                  {m.tasks.map((task, j) => (
                    <li key={j}>
                      <LuCircleCheck size={13} style={{ color: m.color, flexShrink: 0 }} />
                      {task}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          {/* Image + stats row */}
          <div className="maint-bottom-row reveal">
            <div className="maint-img-wrap">
              <img src={IMG.predictive} alt="Predictive maintenance dashboard" loading="lazy" />
            </div>
            <div className="maint-info-stack">
              <div className="maint-info-card" style={{ background: 'linear-gradient(135deg,var(--primary-pale),var(--accent-pale))', border: '1px solid rgba(124,58,237,.2)' }}>
                <div style={{ color: 'var(--primary)', marginBottom: 10 }}><LuTrophy size={28} /></div>
                <div style={{ fontSize: '1rem', fontWeight: 700, color: 'var(--text-2)', marginBottom: 8 }}>Industry-Leading Uptime</div>
                <div style={{ fontSize: '.86rem', color: 'var(--text-3)', lineHeight: 1.8 }}>
                  World-class airports achieve <strong style={{ color: 'var(--primary)' }}>99.95% system uptime</strong> —
                  meaning less than 4.5 hours of unplanned downtime annually across the entire baggage network.
                </div>
              </div>
              <div className="maint-info-card" style={{ background: 'var(--card-bg)', border: '1px solid var(--border-light)', boxShadow: 'var(--shadow-sm)' }}>
                <div style={{ color: 'var(--accent)', marginBottom: 10 }}><LuUsers size={28} /></div>
                <div style={{ fontSize: '1rem', fontWeight: 700, color: 'var(--text-2)', marginBottom: 8 }}>Trained Professional Teams</div>
                <div style={{ fontSize: '.86rem', color: 'var(--text-3)', lineHeight: 1.8 }}>
                  Certified maintenance technicians and engineers work around the clock,
                  with <strong style={{ color: 'var(--accent)' }}>continuous training</strong> on the latest technologies.
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <div className="divider" />

      {/* ══ DOWNLOAD TECHNICAL BROCHURE SECTION ══ */}
      <section className="page-section">
        <div className="container">
          <div style={{
            background: 'linear-gradient(135deg, var(--primary-pale) 0%, var(--accent-pale) 100%)',
            borderRadius: 24,
            padding: 48,
            border: '1px solid rgba(124,58,237,.2)',
            position: 'relative',
            overflow: 'hidden',
          }} className="reveal">
            <div style={{ position: 'absolute', top: -50, right: -50, width: 200, height: 200, background: 'radial-gradient(circle, rgba(124,58,237,.15) 0%, transparent 70%)', borderRadius: '50%' }}></div>
            <div style={{ position: 'absolute', bottom: -30, left: -30, width: 150, height: 150, background: 'radial-gradient(circle, rgba(8,145,178,.15) 0%, transparent 70%)', borderRadius: '50%' }}></div>

            <div style={{ position: 'relative', zIndex: 1, textAlign: 'center' }}>
              <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 20 }}>
                <div style={{
                  width: 72,
                  height: 72,
                  borderRadius: 20,
                  background: 'linear-gradient(135deg, var(--primary), var(--accent))',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#fff',
                  boxShadow: '0 10px 40px rgba(124,58,237,.3)',
                }}>
                  <LuFileText size={36} />
                </div>
              </div>

              <h2 style={{
                fontFamily: 'var(--font-display)',
                fontSize: '2rem',
                fontWeight: 800,
                color: 'var(--text-2)',
                marginBottom: 12,
              }}>
                Download the Technical Brochure
              </h2>

              <p style={{
                fontSize: '1rem',
                color: 'var(--text-3)',
                maxWidth: 600,
                margin: '0 auto 28px',
                lineHeight: 1.7,
              }}>
                Get our comprehensive 60-page guide covering safety systems, emergency protocols,
                predictive maintenance, and the complete airport conveyor belt architecture.
              </p>

              <div style={{
                display: 'flex',
                gap: 24,
                justifyContent: 'center',
                flexWrap: 'wrap',
                marginBottom: 28,
              }}>
                {[
                  { icon: <LuFileText size={18} />, label: '60 Pages', desc: 'Comprehensive guide' },
                  { icon: <LuRadio size={18} />, label: 'HD Diagrams', desc: 'System architecture' },
                  { icon: <LuLock size={18} />, label: 'Free', desc: 'No registration' },
                ].map((item, i) => (
                  <div key={i} style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 10,
                    padding: '12px 20px',
                    background: 'var(--card-bg)',
                    borderRadius: 12,
                    border: '1px solid var(--border-light)',
                  }}>
                    <div style={{ color: 'var(--primary)' }}>{item.icon}</div>
                    <div style={{ textAlign: 'left' }}>
                      <div style={{ fontWeight: 700, fontSize: '.9rem', color: 'var(--text-2)' }}>{item.label}</div>
                      <div style={{ fontSize: '.78rem', color: 'var(--text-muted)' }}>{item.desc}</div>
                    </div>
                  </div>
                ))}
              </div>

              <div style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap' }}>
                <Link to="/brochure" className="btn btn-primary btn-lg" style={{ minWidth: 220 }}>
                  <LuDownload size={17} /> Download Brochure <LuArrowRight size={15} />
                </Link>
                <Link to="/contact" className="btn btn-outline btn-lg">
                  <LuUsers size={17} /> Contact Our Team
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      <div className="divider" />

      {/* ══ CTA ══ */}
      <section className="page-section-sm">
        <div className="container text-center reveal">
          <h2 className="section-title">Explore More About Airport Systems</h2>
          <p className="section-sub" style={{ margin: '0 auto 32px' }}>
            Dive deeper into the technology, operations, and infrastructure that keep airports running.
          </p>
          <div style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap' }}>
            <Link to="/technology" className="btn btn-primary btn-lg">
              <LuCpu size={17} /> Technology <LuArrowRight size={15} />
            </Link>
            <Link to="/how-it-works" className="btn btn-outline btn-lg">
              <LuWrench size={17} /> How It Works
            </Link>
            <Link to="/types" className="btn btn-outline btn-lg">
              <LuActivity size={17} /> System Types
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}

export default Safety;
